import CONSTANTS from './constants';
import { degreeToRadian, radianToDegree } from './index';

export const filterNonNullData = (data, key) => {
  return data.filter((elt) => {
    return elt[key];
  });
};

export const getSignalStatusWithLastRecord = (
  data,
  options = { testEnv: { time: 0 } }
) => {
  const defaultKey = CONSTANTS.SIGNALS[0];
  const filteredData = filterNonNullData(data, defaultKey);

  // Sort values about epoch
  const sortedData = filteredData.sort((a, b) => {
    if (a.epoch > b.epoch) {
      return 1;
    }
    if (a.epoch < b.epoch) {
      return -1;
    }
    return 0;
  });

  if (sortedData.length === 0) {
    return {
      status: CONSTANTS.SIGNALS_STATUS.DISCONNECTED,
      epoch: null,
      value: null,
    };
  }
  // Get the more recent value
  const lastRecord = sortedData[sortedData.length - 1];
  const lastEpoch = lastRecord.epoch;
  const lastValue = lastRecord[defaultKey];

  // Get now time.
  // If test, get time from params
  let testTime = null;
  if (options && options.testEnv) {
    testTime = options.testEnv.time;
  }
  const now = testTime || new Date().getTime();

  const diff = now - lastEpoch;

  let status = null;
  // Return the values
  if (diff < 65 * 1000) {
    status = CONSTANTS.SIGNALS_STATUS.OK;
  } else if (diff < 5 * 60 * 1000) {
    status = CONSTANTS.SIGNALS_STATUS.LOW;
  } else {
    status = CONSTANTS.SIGNALS_STATUS.DISCONNECTED;
  }

  return { status, epoch: lastEpoch, value: lastValue };
};

/**
 * Checks when the last data was return
 * - OK <65s
 * - LOW <5min
 * - DISCONNECTED >5min
 * @param {*} data
 * @param { testEnv = { time: 0 } } options for testing
 */
export const getSignalStatus = (data, options = { testEnv: { time: 0 } }) => {
  const fullStatus = getSignalStatusWithLastRecord(data, options);
  return fullStatus.status;
};

const getLastKeyData = (signalData, key) => {
  const lastSignalKeyData = signalData && signalData[key];
  if (!key || !lastSignalKeyData || !lastSignalKeyData[key]) {
    return null;
  }
  return lastSignalKeyData;
};

const getLastData = (signalData, key) => {
  const lastKeyData = getLastKeyData(signalData, key);
  if (!lastKeyData) {
    return null;
  }
  const lastValue = lastKeyData[key];
  const lastEpoch = lastKeyData.epoch;
  const { health } = lastKeyData;

  return { epoch: lastEpoch, value: lastValue, health };
};

export const getLastSignalStatus = (signalData, key = CONSTANTS.SIGNALS[0]) => {
  let status = CONSTANTS.SIGNALS_STATUS.DISCONNECTED;

  const lastData = getLastData(signalData, key);
  if (!lastData) {
    return { status, epoch: null, value: null, health: 0 };
  }
  const { epoch, value, health } = lastData;

  const now = new Date().getTime();
  const diff = now - epoch;

  // Return the values
  if (diff < 10 * 60 * 1000) {
    // FIXME was 65
    status = CONSTANTS.SIGNALS_STATUS.OK;
  } else if (diff < 15 * 60 * 1000) {
    status = CONSTANTS.SIGNALS_STATUS.LOW;
  } else {
    status = CONSTANTS.SIGNALS_STATUS.DISCONNECTED;
  }
  return { status, epoch, value, health };
};

// export const getLastSignalStatus = (signalData, key = CONSTANTS.SIGNALS[0]) => {
//   return getLastStatus(signalData, key);
// };

export const getLastBatteryStatus = (
  signalData,
  key = CONSTANTS.BATTERIES[0]
) => {
  let status = CONSTANTS.BATTERY_STATUS.LOW;

  const lastData = getLastData(signalData, key);
  if (!lastData) {
    return { status, epoch: null, value: null, health: 0 };
  }
  const { epoch, value, health } = lastData;

  // Return the values
  if (value > 12) {
    status = CONSTANTS.BATTERY_STATUS.OK;
  } else {
    status = CONSTANTS.BATTERY_STATUS.LOW;
  }
  return { status, epoch, value, health };
};
export const getLastBatteryStatusV3 = (
  signalData,
  key = CONSTANTS.BATTERIES[0]
) => {
  let status = CONSTANTS.BATTERY_STATUS.LOW;

  const lastData = getLastData(signalData, key);
  if (!lastData) {
    return { status, epoch: null, value: null, health: 0 };
  }
  const { epoch, value, health } = lastData;

  // Return the values
  if (value > 3.8) {
    status = CONSTANTS.BATTERY_STATUS.OK;
  } else {
    status = CONSTANTS.BATTERY_STATUS.LOW;
  }
  return { status, epoch, value, health };
};

// FIXME : Is the data correct ? Check it.
export const getLastLocalesStatus = (signalData, key = 'lnbTemperature') => {
  let status = CONSTANTS.BASE_STATUS.DISCONNECTED;
  const lastData = getLastData(signalData, key);
  if (!lastData) {
    return { status, epoch: null, value: null, health: 0 };
  }
  const { epoch, value, health } = lastData;

  const now = new Date().getTime();
  const diff = now - epoch;

  // Return the values
  if (!epoch) {
    status = CONSTANTS.BASE_STATUS.DISCONNECTED;
  } else if (diff < 5 * 60 * 1000) {
    status = CONSTANTS.BASE_STATUS.OK;
  } else {
    status = CONSTANTS.BASE_STATUS.LOW;
  }
  return { status, epoch, value, health };
};

export const getStatusWithLastRecord = (
  data,
  dataKey,
  defaultStatus = null
) => {
  const filteredData = filterNonNullData(data, dataKey);

  // Sort values about epoch
  const sortedData = filteredData.sort((a, b) => {
    if (a.epoch > b.epoch) {
      return 1;
    }
    if (a.epoch < b.epoch) {
      return -1;
    }
    return 0;
  });

  if (sortedData.length === 0) {
    return {
      status: defaultStatus,
      epoch: null,
      value: null,
    };
  }
  // Get the more recent value
  const lastRecord = sortedData[sortedData.length - 1];
  const lastEpoch = lastRecord.epoch;
  const lastValue = lastRecord[dataKey];

  const status = null;
  // TODO
  // Determine the battery status

  return { status, epoch: lastEpoch, value: lastValue };
};

/**
 *
 * @param {*} dataframe
 * From { "WLU": { 0:12, 1: 38, ...}}
 * returns
 * [ { WLU: 12, epoch: 153, ...}, { WLU: 38 ...}]
 */
export const getArrayFromDataframe = (dataframe) => {
  const data = [];

  const keys = Object.keys(dataframe);
  const firstKey = keys[0];
  if (firstKey) {
    const dfLength = Object.keys(dataframe[firstKey]).length;

    for (let i = 0; i < dfLength; i += 1) {
      const item = {};
      keys.forEach((key) => {
        item[key] = dataframe[key][i];
      });
      data.push(item);
    }
  }
  return data;
};

const convertToHexadecimal = (serialNumber) => {
  if (!serialNumber) {
    return null;
  }
  const hexa = parseInt(serialNumber, 10).toString(16);

  return hexa;
};
export const getHexSerialNumber = (serialNumber) => {
  if (!serialNumber) {
    return null;
  }
  const hSerialNumber = convertToHexadecimal(serialNumber);

  // Add prefix 0, until there is 6 digit
  let hSerialNumberStr = `${hSerialNumber}`;
  while (hSerialNumberStr.length < 6) {
    hSerialNumberStr = `0${hSerialNumberStr}`;
  }
  const fullSerialNumber = `01${hSerialNumberStr}`;
  return fullSerialNumber;
};

/**
 *
 * @param {*} station
 * @param {
 *  signal:{
 *    0100009c : {
 *      NLH:{ epoch: XX, NLH:XX}
 *    }
 *  },
 *  status: {...}
 * } - stationsLastData
 */
export const getLastStatusFromLastData = (
  stationsLastData,
  hexSerialNumber
) => {
  // Add status (signal & battery) to entities
  const lastStatus = { signal: null, status: null, locales: null };

  // Get last signal status from signal data
  if (stationsLastData && stationsLastData.signal) {
    const stationSignalData = stationsLastData.signal[hexSerialNumber];
    lastStatus.signal = getLastSignalStatus(stationSignalData);
  }

  // Status data
  if (stationsLastData && stationsLastData.status) {
    const stationStatusData = stationsLastData.status[hexSerialNumber];
    if (hexSerialNumber.startsWith('03')) {
      lastStatus.status = getLastBatteryStatusV3(stationStatusData);
    } else {
      lastStatus.status = getLastBatteryStatus(stationStatusData);
    }
  }

  // Locales data
  if (stationsLastData && stationsLastData.locales) {
    const stationLocalesData = stationsLastData.locales[hexSerialNumber];

    lastStatus.locales = getLastLocalesStatus(stationLocalesData);
  }

  return lastStatus;
};
export const getLastStatus = (lastStatus, key) => {
  if (!lastStatus || !key) {
    return null;
  }

  const lastStatusObject = lastStatus && lastStatus[key];
  return lastStatusObject;
};

/**
 *
 * @param {*} {signal: {status:"...", value: "...", epoch: "..."}, status: {...}, locales: {...} }
 * @param {*} key : one of: signal, status, locales
 */
export const getLastStatusValue = (lastStatus, key) => {
  if (!lastStatus || !key) {
    return null;
  }

  const lastStatusValue =
    lastStatus && lastStatus[key] && lastStatus[key].status;
  return lastStatusValue;
};

/**
 *
 * @param {*} {signal: {status:"...", value: "...", epoch: "..."}, status: {...}, locales: {...} }
 * @param {*} key : one of: signal, status, locales
 * @return 0 LOW,
 */
export const getLastStatusValueAsInt = (lastStatus, key) => {
  if (!lastStatus || !key) {
    return -1;
  }
  const lastStatusValue = getLastStatusValue(lastStatus, key);

  let valuesAsInt = {};
  if (key === 'signal') {
    valuesAsInt = CONSTANTS.SIGNALS_STATUS_AS_INT;
  } else if (key === 'status') {
    valuesAsInt = CONSTANTS.BATTERY_STATUS_AS_INT;
  } else if (key === 'locales') {
    valuesAsInt = CONSTANTS.BASE_STATUS_AS_INT;
  }
  const resAsInt = valuesAsInt[lastStatusValue] || -1;
  return resAsInt;
};

export const processSatelliteAzimut = (
  latitude,
  longitude,
  orbitalPosition
) => {
  const latitudeAsRadian = degreeToRadian(latitude);
  const orbitalPositionAsInt = parseInt(orbitalPosition, 10);
  const g = degreeToRadian(orbitalPositionAsInt - longitude);
  const azimut =
    180 - radianToDegree(Math.atan(Math.tan(g) / Math.sin(latitudeAsRadian)));
  return azimut;
};

export const processSatelliteElevation = (
  latitude,
  longitude,
  orbitalPosition
) => {
  const latitudeAsRadian = degreeToRadian(latitude);
  const orbitalPositionAsInt = parseFloat(orbitalPosition);
  const g = degreeToRadian(orbitalPositionAsInt - longitude);
  const elevation = radianToDegree(
    Math.atan(
      (Math.cos(g) * Math.cos(latitudeAsRadian) - 0.1512) /
        Math.sqrt(
          1 -
            Math.cos(g) *
              Math.cos(g) *
              Math.cos(latitudeAsRadian) *
              Math.cos(latitudeAsRadian)
        )
    )
  );
  return elevation;
};
